import React, { useEffect, useState, useMemo } from "react";
import { observer } from "mobx-react-lite";
import {
  Box,
  Typography,
  Paper,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Card,
  CardContent,
  Divider,
} from "@mui/material";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import { useParams } from "react-router-dom";
import MonthlyPicker from "./MonthlyPicker";
import overpressureEventStore from "../../../../stores/OverpressureEventStore";

const DeviceOverpressure = observer(() => {
  const { pimios_id } = useParams();
  const actualDeviceId = pimios_id?.split("-")[0];
  const [selectedMonth, setSelectedMonth] = useState(() =>
    new Date().toISOString().slice(0, 7)
  );
  const month = selectedMonth;
  const eventsForMonth = overpressureEventStore.getEventsForMonth(
    month,
    actualDeviceId
  );
  const events = [...eventsForMonth.v1, ...eventsForMonth.v2];

  console.log("Here are the events in DeviceOverpressure: ", events);

  useEffect(() => {
    if (
      actualDeviceId &&
      !overpressureEventStore.overpressureEventsMap.has(actualDeviceId)
    ) {
      overpressureEventStore.loadOverpressureEventsForDevice(actualDeviceId);
    }
  }, [actualDeviceId]);

  const getSafeDate = (evt) => {
    const raw = evt?.timestamp?.$date ?? evt?.timestamp;
    if (!raw) return null;
    const d = new Date(raw);
    return isNaN(d.getTime()) ? null : d;
  };

  const chartData = useMemo(() => {
    if (!events?.length) return [];
    const byMonth = {};

    events.forEach((e) => {
      const date = getSafeDate(e);
      if (!date) return;

      const key = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}`;
      const valve = e.metadata?.valve || "unknown";

      if (!byMonth[key]) byMonth[key] = { month: key, v1: 0, v2: 0 };
      if (valve === "v1") byMonth[key].v1++;
      if (valve === "v2") byMonth[key].v2++;
    });

    return Object.values(byMonth)
      .sort((a, b) => (a.month < b.month ? 1 : -1))
      .slice(0, 3)
      .reverse();
  }, [events]);

  return (
    <>
      <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 2 }}>
        <MonthlyPicker
          value={selectedMonth}
          onChange={setSelectedMonth}
          label="Month"
        />
      </Box>
      <Box sx={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
        {/* Left Section - Chart */}
        <Card
          elevation={4}
          sx={{
            flex: 1,
            minWidth: 400,
            background: "#f9fafb",
            borderRadius: 3,
            border: "1px solid #e0e0e0",
          }}
        >
          <CardContent>
            <Typography
              variant="h6"
              gutterBottom
              sx={{ fontWeight: "bold", color: "#1976d2" }}
            >
              Overpressure Summary for ${selectedMonth}
            </Typography>
            <Divider sx={{ mb: 2 }} />
            {chartData.length === 0 ? (
              <Typography color="text.secondary">
                No overpressure events recorded.
              </Typography>
            ) : (
              <ResponsiveContainer width="100%" height={300}>
                <BarChart
                  data={chartData}
                  margin={{ top: 10, right: 10, left: 0, bottom: 5 }}
                >
                  <CartesianGrid stroke="#f0f0f0" strokeDasharray="3 3" />
                  <XAxis dataKey="month" stroke="#555" />
                  <YAxis stroke="#555" />
                  <Tooltip />
                  <Legend wrapperStyle={{ color: "#000" }} />
                  <Bar dataKey="v1" fill="#1976d2" name="Valve 1" />
                  <Bar dataKey="v2" fill="#2e7d32" name="Valve 2" />
                </BarChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>

        {/* Right Section - Table */}
        <Paper
          elevation={6}
          sx={{
            flex: 2,
            minWidth: 500,
            p: 3,
            background: "#f1f5f9",
            borderRadius: 3,
          }}
        >
          <Typography
            variant="h6"
            gutterBottom
            sx={{ fontWeight: "bold", color: "#0f172a" }}
          >
            Overpressure Event History
          </Typography>
          <Divider sx={{ mb: 2 }} />

          {events.length === 0 ? (
            <Typography color="text.secondary">
              No overpressure history for this device.
            </Typography>
          ) : (
            <Table size="small">
              <TableHead>
                <TableRow sx={{ backgroundColor: "#e2e8f0" }}>
                  <TableCell sx={{ fontWeight: "bold" }}>Timestamp</TableCell>
                  <TableCell sx={{ fontWeight: "bold" }}>Valve</TableCell>
                  <TableCell sx={{ fontWeight: "bold" }}>Value (psi)</TableCell>
                  <TableCell sx={{ fontWeight: "bold" }}>
                    Threshold (psi)
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {events.map((e) => (
                  <TableRow
                    key={typeof e._id === "object" ? e._id?.$oid : e._id}
                    sx={{
                      "&:hover": {
                        backgroundColor: "#f9fafb",
                      },
                    }}
                  >
                    <TableCell sx={{ fontFamily: "monospace" }}>
                      {(() => {
                        const d = getSafeDate(e);
                        return d ? d.toLocaleString() : "Invalid Date";
                      })()}
                    </TableCell>
                    <TableCell sx={{ fontFamily: "monospace" }}>
                      {e.metadata?.valve?.toUpperCase()}
                    </TableCell>
                    <TableCell
                      sx={{
                        fontFamily: "monospace",
                        color: e.value > e.threshold ? "#ef4444" : "#0f766e",
                        fontWeight: e.value > e.threshold ? "bold" : "normal",
                      }}
                    >
                      {e.value}
                    </TableCell>
                    <TableCell sx={{ fontFamily: "monospace" }}>
                      {e.threshold}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </Paper>
      </Box>
    </>
  );
});

export default DeviceOverpressure;
